import type { BackendCard } from './popularCardApply.ts'

export type ToolPage = 'image' | 'video' | 'audio' | 'trends' | 'photostudio'

// Кросс-разделы ('photostudio', 'trends') — в конце: у карточки на нескольких
// лейнах приоритет остаётся за профильной медиа-страницей; «чистый» тренд
// получает URL /bots/trends/tool/…, «чистая» студийная карточка —
// /bots/images/photostudio/tool/…
const PRIMARY_ORDER: ToolPage[] = ['image', 'video', 'audio', 'photostudio', 'trends']

// Значение — сегменты пути после /bots/. У Фотостудии их два: раздел живёт
// вкладкой внутри изображений. Сегмент переименован со `styles` на
// `photostudio` вслед за названием вкладки (2026-08-03). Старые адреса
// отвечают редиректом: 301 в nginx для внешних ссылок и клиентский
// PhotostudioPathRedirect в App.tsx для переходов внутри SPA.
const PAGE_TO_SECTION: Record<ToolPage, string> = {
	image: 'images',
	video: 'video',
	audio: 'audio',
	trends: 'trends',
	photostudio: 'images/photostudio',
}

export function cardVisibleOn(card: BackendCard, page: ToolPage): boolean {
	return (
		(!card.target_pages?.length || card.target_pages.includes(page)) &&
		!card.hidden_pages?.includes(page)
	)
}

export function cardPrimaryPage(card: BackendCard): ToolPage | null {
	for (const p of PRIMARY_ORDER) {
		if (card.target_pages?.includes(p) && !card.hidden_pages?.includes(p)) return p
	}
	return null
}

/** Карточка из шаблона флоу: её «лендинг» — сам шаблон на холсте, без /tool/slug. */
export function flowTemplateUrl(card: Pick<BackendCard, 'card_kind' | 'flow_template_id'>): string | null {
	if (card.card_kind !== 'flow') return null
	return card.flow_template_id ? `/flows/t/${card.flow_template_id}` : null
}

export function cardPrimaryUrl(card: BackendCard): string | null {
	if (card.card_kind === 'flow') return flowTemplateUrl(card)
	if (!card.slug) return null
	const page = cardPrimaryPage(card)
	if (!page) return null
	return `/bots/${PAGE_TO_SECTION[page]}/tool/${card.slug}`
}

/** Ссылка на инструмент по «худой» карточке из истории генераций.
 *
 *  Бэк отдаёт в `tool_card` (см. /files и /user/history) только slug/тайтл/
 *  страницы — этого достаточно для канонического URL лендинга. Нужна там, где
 *  промпт генерации скрыт: вместо «Воссоздать» (подставила бы наш промпт в
 *  бар) юзер уходит на сам инструмент и повторяет через него.
 *
 *  null — карточку удалили из админки или у неё нет slug: кнопку не рисуем.
 */
export function toolCardRefUrl(
	ref: { slug?: string | null; title?: string | null; target_pages?: string[] | null; hidden_pages?: string[] | null } | null | undefined,
): string | null {
	if (!ref?.slug) return null
	return cardPrimaryUrl({
		id: '',
		title: ref.title || '',
		slug: ref.slug,
		target_pages: ref.target_pages || undefined,
		hidden_pages: ref.hidden_pages || undefined,
	})
}

/** СТРАНИЧНЫЙ URL карточки — лендинг в контексте ТЕКУЩЕГО раздела:
 *  клик/href на витрине не уводят юзера на другую страницу, когда карточка
 *  живёт на нескольких лейнах (жалоба: клик тренда прыгал на /bots/images).
 *  SEO не страдает: canonical лендинга всегда указывает на cardPrimaryUrl. */
export function cardPageUrl(card: BackendCard, page: ToolPage): string | null {
	if (card.card_kind === 'flow') return flowTemplateUrl(card)
	if (!card.slug || !cardVisibleOn(card, page)) return null
	return `/bots/${PAGE_TO_SECTION[page]}/tool/${card.slug}`
}

/** URL лендинга С КОНТЕКСТОМ витрины: /bots/<раздел>/<ctx>/tool/<slug>.
 *
 *  ctx — активный пебл (категория инструментов / 'all' / 'popular'). Он
 *  возвращает юзера ровно туда, откуда он кликнул: без него клик из категории
 *  уводил на корневой /tool/<slug>, страница читала пустой контекст и
 *  сбрасывала пебл на «Все» — за виджетом оказывалась чужая сетка (жалоба
 *  владельца 2026-07-24). Пусто/нет слага → корневой (канонический) путь;
 *  canonical лендинга ВСЕГДА cardPrimaryUrl, поэтому дублей для поиска нет.
 */
export function cardCtxUrl(card: BackendCard, page: ToolPage, ctx?: string | null): string | null {
	const base = cardPageUrl(card, page)
	if (!base || !ctx) return base
	const section = PAGE_TO_SECTION[page]
	return base.replace(`/bots/${section}/tool/`, `/bots/${section}/${ctx}/tool/`)
}

/** URL лендинга для перехода ИЗ ПОИСКА.
 *
 *  Поиск шире пеблов: найденная карточка может лежать в категории, которая
 *  сейчас не открыта, или вовсе быть скрыта из «Все». Поэтому ctx берём не с
 *  витрины, а с самой карточки — её категорию на этой странице, а если
 *  категории нет — «Все». Иначе после перехода плитки в сетке не окажется, и
 *  якорь лендинга (скролл + подсветка) не к чему было бы привязать.
 */
export function cardSearchHitUrl(card: BackendCard, page: ToolPage): string | null {
	return cardCtxUrl(card, page, card.category_by_page?.[page] || 'all')
}

/** Путь БЕЗ хвоста /tool/<slug> — контекст витрины (категория/all) цел.
 *
 *  null — в пути карточки не было, менять нечего. Нужен там, где инструмент
 *  перестаёт быть активным: крестик виджета и «Создать видео» (адрес держал
 *  старый слаг, и повторный клик по ТОЙ ЖЕ карточке ничего не делал — роутер
 *  не видел смены пути; жалобы владельца 2026-07-24 и 2026-07-25).
 */
export function pathWithoutTool(pathname: string): string | null {
	const toolTail = /\/tool\/[^/]+\/?$/
	if (!toolTail.test(pathname)) return null
	return pathname.replace(toolTail, '') || '/bots'
}

export function catalogPathOf(page: ToolPage): string {
	return `/bots/${PAGE_TO_SECTION[page]}`
}

export function toolCardSeoTitle(card: BackendCard): string {
	return card.seo_title || `${card.title} — нейросеть онлайн | Umnik.AI`
}

export function toolCardSeoDescription(card: BackendCard): string {
	return card.seo_description || card.description || ''
}
